import React, { useEffect, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { CircularProgress, Card, CardContent, Typography, Divider, Box, Grid, MenuItem, Select, Button } from '@mui/material';
import { formatAmount } from '../../utils/formatAmount';
import { formatDate } from '../../utils/dateUtils';
import useCustomerProfile from './useCustomerProfile';
import { toast } from 'react-toastify';

const NewProfileOrder = () => {
  const [profileRowData, setProfileRowData] = useState([]); // For left-side profile table
  const [orderRowData, setOrderRowData] = useState([]); // For right-side order table
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [summaryData, setSummaryData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [productGroup, setProductGroup] = useState(null)
  const [showNoGroup, setShowNoGroup] = useState(true); // Toggle state

  const { profiles, getNewCustomerOrder, updateProfileProductGroup } = useCustomerProfile();

  const profileColumnDefs = [
    { headerName: 'Contact', field: 'contact', flex: 2 },
    { headerName: 'Register Date', field: 'registerDate', flex: 1, valueFormatter: ({ value }) => formatDate(value) },
  ];


  const LinkRenderer = (params) => {
    return (
      <div>
        <a target="_blank" href={params.data.url}>{params.data.url}</a>
      </div>
    )
  }

  const handleSelectGroup = async () => {
    if (productGroup) {
      const response = await updateProfileProductGroup({ contact: selectedProfile.contact, productGroup });
      if (response) {
        toast.success('Update successful');
        refreshData();
      } else {
        toast.error('Update failed');
      }
    } else {
      toast.error('Please select Product Group first');
    }
  };

  const refreshData = async () => {
    setIsLoading(true);
    await getNewCustomerOrder(); // Fetch updated data
    setSelectedProfile(null);
    setIsLoading(false);
  };

  useEffect(() => {
    setIsLoading(true);
    if (profiles && profiles.length > 0) {
      setProfileRowData(profiles);
    }
    setIsLoading(false);
  }, [profiles]);

  useEffect(() => {
    getNewCustomerOrder();
  }, []);

  const handleProfileClick = (event) => {
    const profile = event.data;
    setSelectedProfile(profile);

    if (profile && profile.Orders) {
      // Set order details for the selected profile
      setOrderRowData(profile.Orders);

      // Calculate the summary row
      const totalAmount = profile.Orders.reduce((sum, order) => sum + (parseFloat(order.amount) || 0), 0);
      const totalDeliveryFee = profile.Orders.reduce((sum, order) => sum + (parseFloat(order.deliveryFeeJp) || 0), 0);
      const totalWeight = profile.Orders.reduce((sum, order) => sum + (parseFloat(order.weight) || 0), 0);

      setSummaryData([
        {
          orderDate: null,
          url:null,
          amount: totalAmount.toFixed(2), // Ensure numbers are formatted
          deliveryFeeJp: totalDeliveryFee.toFixed(2),
          weight: totalWeight.toFixed(2),
          productRate: null, // Blank cell for productRate
        },
      ]);
    }
  };

  const toggleRowData = showNoGroup
  ? (profiles?.filter((profile) => !profile.productGroup) || []) // Profiles without productGroup
  : (profiles || []); // All profiles


  return (
    <div className="ag-theme-alpine mx-auto" style={{ width: '100%' }}>
      <h2>Customer Orders</h2>
      <div style={{ display: 'flex', marginBottom: '10px' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setShowNoGroup(!showNoGroup)}
        >
          {showNoGroup ? 'Show All Profiles' : 'Show No Product Group'}
        </Button>
      </div>
      <div className="grid grid-cols-5 gap-x-3">
        {/* Left Side: Profile Table */}
        <div className="col-span-2">
          <div style={{ height: '80vh' }}>
            {isLoading ? (
              <CircularProgress />
            ) : (
              <AgGridReact
                columnDefs={profileColumnDefs}
                rowData={toggleRowData}
                gridOptions={{ onRowClicked: handleProfileClick }}
                defaultColDef={{ resizable: true }}
              />
            )}
          </div>
        </div>

        {/* Right Side: Profile and Order Details */}
        <div className="col-span-3">
          {selectedProfile ? (
            <Card className="p-4 shadow-lg rounded-lg border">
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  Profile and Order Details
                </Typography>
                <Divider style={{ margin: '10px 0' }} />

                {/* Profile Details */}
                <Typography variant="h6" gutterBottom>
                  Profile Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center">
                      <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>Contact:</Typography>
                      <Typography variant="body1" style={{ marginLeft: 8 }}>{selectedProfile.contact}</Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center">
                      <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>Register Date:</Typography>
                      <Typography variant="body1" style={{ marginLeft: 8 }}>{formatDate(selectedProfile.registerDate)}</Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center">
                      <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>Channel:</Typography>
                      <Typography variant="body1" style={{ marginLeft: 8 }}>{(selectedProfile.channel)}</Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center">
                      <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>กลุ่มสินค้า:</Typography>
                      <Typography variant="body1" style={{ marginLeft: 8 }}>{(selectedProfile.productGroup)}</Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Divider style={{ margin: '20px 0' }} />

                {/* Order Details */}
                <Typography variant="h6" gutterBottom>
                  Orders
                </Typography>
                <div style={{ height: '300px', width: '100%' }} className="ag-theme-alpine">
                <AgGridReact
                  columnDefs={[
                    { headerName: 'Order Date', field: 'orderDate', flex: 1, valueFormatter: ({ value }) => formatDate(value) },
                    { 
                      headerName: 'Url', 
                      field: 'url', 
                      flex: 1, 
                      cellRenderer: LinkRenderer, 
                    },
                    { headerName: 'Amount', field: 'amount', flex: 1, valueFormatter: ({ value }) => `${formatAmount(value)} ¥` },
                    { headerName: 'Delivery Fee', field: 'deliveryFeeJp', flex: 1, valueFormatter: ({ value }) => `${formatAmount(value)} ¥` },
                    { headerName: 'Weight', field: 'weight', flex: 1 },
                    { headerName: 'Product Rate', field: 'productRate', flex: 1 },
                  ]}
                  rowData={orderRowData} // Orders for the selected profile
                  pinnedBottomRowData={summaryData} // Summary row
                  defaultColDef={{ resizable: true }}
                  suppressMovableColumns
                />
                </div>
                <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>กลุ่มสินค้า</Typography>
                <Select
                  value={productGroup}
                  onChange={(e) => setProductGroup(e.target.value)}
                  variant="outlined"
                >
                  <MenuItem value='ไม้กอล์ฟ'>ไม้กอล์ฟ</MenuItem>
                  <MenuItem value='อะไหล่รถยนต์'>อะไหล่รถยนต์</MenuItem>
                  <MenuItem value='เครื่องดนตรี'>เครื่องดนตรี</MenuItem>
                  <MenuItem value='ฟิกเกอร์/ของเล่น'>ฟิกเกอร์/ของเล่น</MenuItem>
                  <MenuItem value='การ์ด'>การ์ด</MenuItem>
                  <MenuItem value='ของใช้ทั่วไป'>ของใช้ทั่วไป</MenuItem>
                  <MenuItem value='เสื้อผ้า/รองเท้า'>เสื้อผ้า/รองเท้า</MenuItem>
                  <MenuItem value='ของกิน'>ของกิน</MenuItem>
                  <MenuItem value='อื่นๆ'>อื่นๆ</MenuItem>
                  <MenuItem value='ฝากส่ง'>ฝากส่ง</MenuItem>
                </Select>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSelectGroup}
                  style={{ marginLeft: 20 }}
                >
                  Update
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Typography variant="h6" color="textSecondary">
              Select a profile to view details
            </Typography>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewProfileOrder;
